import { useMemo } from 'react';
import { createRouterSelector } from '@atlassian/react-resource-router/src/controllers/router-store';
import { AsyncHorizontalOverviewNav } from '@atlassian/jira-business-horizontal-nav/src/async.tsx';
import { AsyncHorizontalNavJira } from '@atlassian/jira-horizontal-nav/src/async.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useHorizontalNavComponent } from '@atlassian/jira-navigation-apps-horizontal-nav-selector/src/controllers/nav-component-controller/index.tsx';
import {
	useSoftwareProjectTheming,
	getSoftwareThemedRoutes,
} from '@atlassian/jira-project-theme-providers/src/controllers/software-theme/index.tsx';
import {
	getIsSoftwareThemingEnabled,
	getIsSoftwareThemingM2Enabled,
} from '@atlassian/jira-project-theme-providers/src/utils/getIsSoftwareThemingEnabled.tsx';
import { functionWithCondition } from '@atlassian/jira-feature-flagging-utils/src/utils/function-with-condition/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import {
	BUSINESS_PROJECT_THEMED_ROUTES,
	getBusinessProjectForcedInsetRoutes,
	SHARED_THEMED_ROUTES,
} from './constants.tsx';

export const useCurrentRouteName = createRouterSelector((state) => state.route.name);
export const useCurrentRouteGroup = createRouterSelector((state) => state.route.group);
export const useCurrentParams = createRouterSelector((state) => state.match.params);

type ProjectTheme = {
	hasTheming: boolean;
	hasProjectViewNavigation: boolean;
	isInset: boolean;
};

const defaultProjectTheme: ProjectTheme = {
	hasTheming: false,
	hasProjectViewNavigation: false,
	isInset: false,
};

export const useProjectThemeOld = () => {
	const routeName = useCurrentRouteName();
	const routeGroup = useCurrentRouteGroup();

	const HorizontalNavComponent = useHorizontalNavComponent();
	const { hasSoftwareTheming, isSoftwareFullTheming, isSoftwareProject } =
		getIsSoftwareThemingEnabled()
			? // eslint-disable-next-line react-hooks/rules-of-hooks
				useSoftwareProjectTheming()
			: { hasSoftwareTheming: false, isSoftwareFullTheming: false, isSoftwareProject: false };

	const isBusinessRouteGroup = routeGroup === 'classic-business';

	const hasProjectViewNavigation = HorizontalNavComponent != null;

	let hasTheming: boolean;
	const isSoftwareCalendarRoute =
		routeName === 'software-calendar' || routeName === 'software-calendar-classic';

	if (isSoftwareCalendarRoute && !getIsSoftwareThemingM2Enabled()) {
		// remove this condition completely on m2 exp cleanup
		hasTheming = false;
	} else if (isSoftwareProject) {
		hasTheming = hasSoftwareTheming;
	} else {
		hasTheming =
			HorizontalNavComponent === AsyncHorizontalNavJira ||
			HorizontalNavComponent === AsyncHorizontalOverviewNav ||
			routeName === 'issue';
	}

	// Routes showing a themed inset layout are:
	// - all routes with the business project view navigation
	// - select software routes with contoured theming
	// - except for the routes with business group (as they already cater for the theme)
	const isInset = hasTheming && !isBusinessRouteGroup && !isSoftwareFullTheming;

	return {
		isInset: fg('jira_list_nin_merge')
			? isInset || getBusinessProjectForcedInsetRoutes().has(routeName)
			: isInset,
		hasTheming,
		hasProjectViewNavigation,
	};
};

const useProjectThemeSimplified = (): ProjectTheme => {
	const routeName = useCurrentRouteName();
	const routeGroup = useCurrentRouteGroup();
	const isBusinessProject = BUSINESS_PROJECT_THEMED_ROUTES.has(routeName);
	const { allThemedSoftwareProjectRoutes } = getSoftwareThemedRoutes();
	const isSoftwareProject = allThemedSoftwareProjectRoutes.has(routeName);
	const isIssueView = routeName === 'issue';
	const isBusinessRouteGroup = routeGroup === 'classic-business';
	const { hasSoftwareTheming, isSoftwareFullTheming } = getIsSoftwareThemingEnabled()
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useSoftwareProjectTheming()
		: { hasSoftwareTheming: false, isSoftwareFullTheming: false };
	return useMemo(() => {
		if (isIssueView) {
			return {
				hasTheming: true,
				hasProjectViewNavigation: false,
				isInset: true,
			};
		}

		if (!isBusinessProject && !isSoftwareProject) {
			return defaultProjectTheme;
		}

		// Remove this if clause once when cleaning up getIsSoftwareThemingEnabled
		// Will would get contoured theming on project pages if we don't have this check
		if (SHARED_THEMED_ROUTES.has(routeName)) {
			if (routeName === 'project-pages') {
				return {
					hasTheming: true,
					hasProjectViewNavigation: true,
					isInset: false,
				};
			}

			return {
				hasTheming: true,
				hasProjectViewNavigation: true,
				isInset: true,
			};
		}

		if (isSoftwareProject) {
			if (!getIsSoftwareThemingEnabled()) {
				return defaultProjectTheme;
			}

			const isSoftwareCalendarRoute =
				routeName === 'software-calendar' || routeName === 'software-calendar-classic';

			if (isSoftwareCalendarRoute && !getIsSoftwareThemingM2Enabled()) {
				return {
					hasTheming: false,
					hasProjectViewNavigation: true,
					isInset: false,
				};
			}

			return {
				hasTheming: hasSoftwareTheming,
				hasProjectViewNavigation: true,
				isInset: hasSoftwareTheming && !isSoftwareFullTheming,
			};
		}

		if (isBusinessProject) {
			const hasProjectViewNavigation = BUSINESS_PROJECT_THEMED_ROUTES.has(routeName);
			const hasTheming = hasProjectViewNavigation;
			const isInset =
				(hasTheming && !isBusinessRouteGroup) ||
				getBusinessProjectForcedInsetRoutes().has(routeName);

			return {
				hasTheming,
				hasProjectViewNavigation,
				isInset,
			};
		}

		return defaultProjectTheme;
	}, [
		isIssueView,
		isBusinessProject,
		isSoftwareProject,
		routeName,
		isBusinessRouteGroup,
		hasSoftwareTheming,
		isSoftwareFullTheming,
	]);
};

export const useProjectTheme = functionWithCondition(
	() => expVal('performance_improvement_in_use_project_theme', 'isEnabled', false),
	useProjectThemeSimplified,
	useProjectThemeOld,
);
